import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../services/api";
import { useApp } from "../../../services/app-context";
import { SelectButton } from "../../praxis-play/CustomizerModal";
import { twMerge } from "tailwind-merge";
import { MultiSelect } from "../../../components/MultiSelect";
import { getInitial } from "../../../services/utils";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";

export type AssignableUserRow = User & {
  id: string;
  name: string;
  selected: boolean;
};

type User = {
  cohort?: string;
  id: string;
  name: string;
  region?: string;
  territory?: string;
  zone?: string;
}

type UserOption = { label: string; value: string };

function UserList({
  onSelectAll,
  onToggleSelect,
  users,
  isLoading,
  cohorts,
}: {
  onSelectAll: () => void;
  onToggleSelect: (userId: string) => void;
  users: AssignableUserRow[];
  isLoading: boolean;
  cohorts: any[];
}) {
  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      <div className="flex items-center justify-between shrink-0">
        <div className="text-sm font-semibold text-text-gray-500 tracking-wide">
          {users.length} USERS
        </div>
        <button
          type="button"
          className="text-sm font-semibold text-PraxisTerracotta hover:opacity-80 disabled:opacity-40"
          onClick={onSelectAll}
          disabled={!users.length || isLoading}
        >
          Select All
        </button>
      </div>

      <div className="flex flex-col gap-1 overflow-y-auto flex-1 pr-1 -mr-1">
        {isLoading && (
          <div className="text-gray-403 text-sm py-6 text-center">Loading…</div>
        )}
        {!isLoading &&
          users.map((user) => {
            const cohort = cohorts.find((c) => c.key === user.cohort);
            return (
              <label
                key={user.id}
                className={twMerge("flex items-center justify-between gap-3 py-2.5 px-1.5 rounded-lg cursor-pointer", user.selected ? "bg-[#EFF6FF]" : "hover:bg-gray-50")}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={user.selected}
                    onChange={() => onToggleSelect(user.id)}
                    className="w-4 h-4 shrink-0 rounded border-gray-201 accent-gray-701"
                  />
                  <span className="text-sm font-medium max-w-[12vw] text-text-primary truncate">{user.name}</span>
                </div>
                <div className="flex justify-start ">
                  {user.cohort && <div className="text-[10px] font-semibold py-1 px-2.5 rounded-[10px]" style={{ backgroundColor: cohort?.backgroundColor || "#F4F4F5", color: cohort?.textColor || "#71717A" }}>{cohort?.name || user.cohort}</div>}
                </div>
              </label>
            );
          })}
        {!isLoading && users.length === 0 && (
          <div className="text-gray-403 text-sm py-6 text-center">No users found</div>
        )}
      </div>
    </div>
  );
}

function AssignedUsersList({
  userIdToName,
  users,
  onRemove,
  onClearAll,
}: {
  userIdToName: Map<string, string>;
  users: UserOption[];
  onRemove: (userId: string) => void;
  onClearAll: () => void;
}) {
  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      <div className="flex items-center justify-between shrink-0">
        <div className="text-sm font-semibold text-black flex items-center gap-2">
          Assigned Users {users.length > 0 && <div className="bg-gray-701 text-white text-[10px] min-w-4 w-auto h-4 rounded-full flex items-center justify-center">{users.length}</div>}
        </div>
        <button
          type="button"
          className="text-xs font-semibold text-text-primary hover:opacity-80 disabled:opacity-40"
          onClick={onClearAll}
          disabled={!users.length}
        >
          Clear All
        </button>
      </div>
      <div className="flex flex-col gap-1 overflow-y-auto flex-1 pr-1 -mr-1">
        {users.length === 0 && (
          <div className="text-gray-403 text-sm py-6 text-center">
            Select users from the list to assign
          </div>
        )}
        {users.map((user) => {
          const displayName = userIdToName.get(user.value) || user.label;
          return (
          <div
            key={user.value}
            className="flex items-center justify-between gap-2 py-2.5 px-3"
          >
            <div className="flex items-center gap-2">
              <div className="w-[27px] h-[27px] rounded-full flex items-center justify-center bg-gray-402 text-white text-xs">{getInitial(displayName.split(" ")[0], displayName.split(" ")[1])}</div>
              <div className="flex flex-col flex-1">
                <span className="text-xs text-black">{displayName}</span>
              </div>
            </div>
            <button
              type="button"
              aria-label={`Remove ${user.label}`}
              className="shrink-0 p-1 rounded-md hover:bg-gray-100 text-gray-403 hover:text-text-primary"
              onClick={() => onRemove(user.value)}
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          </div>
        )})}
      </div>
    </div>
  );
}

function Filters({
  search,
  onSearchChange,
  cohortFilter,
  onCohortChange,
  zoneFilter,
  onZoneChange,
  regionFilter,
  onRegionChange,
  cohortOptions,
  regionsOptions,
  zonesOptions,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  cohortFilter: string;
  onCohortChange: (v: string) => void;
  zoneFilter: string;
  onZoneChange: (v: string) => void;
  regionFilter: string[];
  onRegionChange: (v: string[]) => void;
  cohortOptions: { label: string; value: string }[];
  regionsOptions: { label: string; value: string }[];
  zonesOptions: { label: string; value: string }[];
}) {
  const { styles: appStyles } = useApp();
  return (
    <div className="flex flex-col gap-3 shrink-0 mb-4">
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-black leading-[15.6px]">Search</span>
        <input
          type="search"
          placeholder="Search users"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full text-sm h-9 border border-gray-201 rounded-lg px-3 py-2 !outline-none bg-white placeholder:text-gray-403"
        />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-black leading-[15.6px]">Zone</span>
        <div className="flex items-center gap-2">
          <SelectButton
            title={"All"}
            key={"all"}
            onClick={() => onZoneChange("all")}
            selected={zoneFilter === "all"}
            className="min-w-[50px] !text-xs !h-6"
            bgClassName="bg-white border !border-lightGray text-black"
          />
          {
            zonesOptions.map((zone) => (
              <SelectButton
                title={zone.label}
                key={zone.value}
                onClick={() => onZoneChange(zone.value)}
                selected={zoneFilter === zone.value}
                className="min-w-[50px] !text-xs !h-6"
                bgClassName="bg-white border !border-lightGray text-black"
              />
            ))
          }
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-black leading-[15.6px]">Region</span>
        <MultiSelect placeholder="Select regions" options={regionsOptions} onChange={(values) => onRegionChange(values)} value={regionFilter} styles={{
          control: (base, state) => ({
            ...base,
            borderRadius: 9,
            borderColor: '#E9E9E9',
            boxShadow: 'none',
            '&:hover': { borderColor: '#E9E9E9' },
          }),
          menu: (base) => ({
            ...base,
            boxShadow: '0 4px 22.4px 0 rgba(0, 0, 0, 0.25)',
          }),
          option: (base, state) => ({
            ...base,
            fontSize: 12,
            textTransform: 'capitalize' as const,
            color: '#000000',
            backgroundColor: state.isFocused ? '#EFF6FF' : 'transparent',
            '&:active': { backgroundColor: '#EFF6FF' },
          }),
          placeholder: (base) => ({
            ...base,
            fontSize: 12,
            color: '#52525B',
          }),
          multiValue: (base) => ({
            ...base,
            border: `1px solid ${appStyles?.colors?.PraxisTerracotta}`,
            backgroundColor: appStyles?.colors?.background2,
            borderRadius: 9999,
            padding: '0px 6px',
          }),
          multiValueLabel: (base) => ({
            ...base,
            color: appStyles?.colors?.PraxisTerracotta,
          }),
          multiValueRemove: (base) => ({
            ...base,
            color: appStyles?.colors?.PraxisTerracotta,
            '&:hover': {
              backgroundColor: 'transparent',
              color: appStyles?.colors?.PraxisTerracotta,
            },
          }),
        }} />
      </div>
      {cohortOptions?.length > 0 && (<div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-black leading-[15.6px]">Cohort</span>
        <div className="flex items-center gap-2">
          <SelectButton
            title={"All"}
            key={"all"}
            onClick={() => onCohortChange("all")}
            selected={cohortFilter === "all"}
            className="min-w-[50px] !text-xs !h-6"
            bgClassName="bg-white border !border-lightGray text-black"
          />
          {
            cohortOptions.map((cohort) => (
              <SelectButton
                title={cohort.label}
                key={cohort.value}
                onClick={() => onCohortChange(cohort.value)}
                selected={cohortFilter === cohort.value}
                className="min-w-[50px] !text-xs !h-6"
                bgClassName="bg-white border !border-lightGray text-black"
              />
            ))
          }
        </div>
      </div>)}
    </div>
  );
}

export function CertificationAssignUserStep({
  form,
  setForm,
}: {
  form: { userList: UserOption[] };
  setForm: React.Dispatch<React.SetStateAction<any>>;
}) {
  const { tenantProfile } = useApp();
  const cohorts = tenantProfile?.groups || [];

  const [search, setSearch] = useState("");
  const [zoneFilter, setZoneFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState<string[]>([]);
  const [cohortFilter, setCohortFilter] = useState("all");
  const [leftSelectedIds, setLeftSelectedIds] = useState<Set<string>>(() => new Set());

  const apiGroup = cohortFilter === "all" ? "" : cohortFilter;

  const { data: usersData, isFetching: isFetchingBase } = useQuery({
    queryKey: ["certification-assign-users", apiGroup],
    queryFn: () => api.searchCertificationsUsers({ search: "", group: apiGroup || undefined }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  /** Full directory (no cohort filter) for resolving assignee display names; shares cache with `usersData` when cohort is "all". */
  const { data: assignUsersDirectoryData } = useQuery({
    queryKey: ["certification-assign-users", ""],
    queryFn: () => api.searchCertificationsUsers({ search: "", group: undefined }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  const { data: regionsData } = useQuery({
    queryKey: ["certification-assign-regions"],
    queryFn: () => api.getRegions(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  const regions = regionsData?.data || [];

  const regionsOptions = useMemo(() => {
    return regions.map((region) => ({
      label: region.name,
      value: region.id,
    }));
  }, [regions]);

  const { data: zonesData } = useQuery({
    queryKey: ["certification-assign-zones"],
    queryFn: () => api.getZones(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });
  const zones = zonesData?.data || [];
  const zonesOptions = useMemo(() => {
    return zones.map((zone) => ({
      label: zone.name,
      value: zone.id,
    }));
  }, [zones]);

  const usersFromApi = useMemo(() => (usersData?.data || []) as User[], [usersData?.data]);

  const rawUsers = useMemo(() => {
    const data = usersFromApi;
    return data.filter((u) => {
      return (cohortFilter === "all" || !cohortFilter || u.cohort === cohortFilter) && (zoneFilter === "all" || u.zone === zoneFilter) && (regionFilter.length === 0 || (u.region != null && regionFilter.includes(u.region)));
    }).filter((u) => u.name.toLowerCase().includes(search.toLowerCase()));
  }, [usersFromApi, search, cohortFilter, zoneFilter, regionFilter]);

  const userIdToName = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of (assignUsersDirectoryData?.data || []) as User[]) {
      if (u?.id && u?.name) m.set(u.id, u.name);
    }
    for (const u of usersFromApi) {
      if (u?.id && u?.name) m.set(u.id, u.name);
    }
    return m;
  }, [assignUsersDirectoryData?.data, usersFromApi]);

  /** Prefill / API may set `label` to the user id; replace with real names once search users load. */
  const assignedUserIdsKey = useMemo(
    () => [...(form.userList || []).map((u) => u.value)].sort().join(","),
    [form.userList]
  );

  useEffect(() => {
    if (!userIdToName.size || !assignedUserIdsKey) return;
    setForm((prev: any) => {
      const current: UserOption[] = prev.userList || [];
      if (!current.length) return prev;
      let changed = false;
      const next = current.map((u) => {
        const name = userIdToName.get(u.value);
        if (!name) return u;
        const labelLooksLikeId = u.label === u.value || !String(u.label ?? "").trim();
        if (labelLooksLikeId && u.label !== name) {
          changed = true;
          return { ...u, label: name };
        }
        return u;
      });
      return changed ? { ...prev, userList: next } : prev;
    });
  }, [userIdToName, assignedUserIdsKey, setForm]);

  const assignedById = useMemo(() => {
    const m = new Map<string, UserOption>();
    (form.userList || []).forEach((u) => m.set(u.value, u));
    return m;
  }, [form.userList]);

  // Left-panel selection stays in local state; when filters change, only re-check rows that are still
  // visible and remain in form.userList. Assigned list is not cleared by filter changes.
  useEffect(() => {
    const visibleIds: Set<string> = new Set(rawUsers.map((u) => u.id));
    const next = new Set<string>();
    visibleIds.forEach((id) => {
      if (assignedById.has(id)) next.add(id);
    });
    setLeftSelectedIds(next);
  }, [apiGroup, rawUsers, assignedById]);

  const listRows: AssignableUserRow[] = useMemo(
    () =>
      rawUsers
        .map((u) => ({
          id: u.id,
          name: u.name,
          selected: leftSelectedIds.has(u.id),
          cohort: u.cohort,
        })),
    [rawUsers, leftSelectedIds]
  );

  const cohortOptions = useMemo(
    () =>
      cohorts.map((c: { name?: string; key: string }) => ({
        label: c.name || c.key || "Cohort",
        value: c.key,
      })),
    [cohorts]
  );

  const upsertAssigned = useCallback(
    (id: string, name: string, add: boolean) => {
      setForm((prev: any) => {
        const current: UserOption[] = prev.userList || [];
        if (!add) {
          return { ...prev, userList: current.filter((u) => u.value !== id) };
        }
        if (current.some((u) => u.value === id)) return prev;
        return {
          ...prev,
          userList: [...current, { label: name, value: id }],
        };
      });
    },
    [setForm]
  );

  const handleToggleSelect = useCallback(
    (userId: string) => {
      const user = rawUsers.find((u) => u.id === userId);
      if (!user) return;
      const isOn = leftSelectedIds.has(userId);
      setLeftSelectedIds((prev) => {
        const next = new Set(prev);
        if (isOn) next.delete(userId);
        else next.add(userId);
        return next;
      });
      upsertAssigned(userId, user.name, !isOn);
    },
    [rawUsers, leftSelectedIds, upsertAssigned]
  );

  const handleSelectAll = useCallback(() => {
    const allSelected = rawUsers.length > 0 && rawUsers.every((u) => leftSelectedIds.has(u.id));
    if (allSelected) {
      setLeftSelectedIds(new Set());
      setForm((prev: any) => {
        const visibleIds = new Set(rawUsers.map((u) => u.id));
        return {
          ...prev,
          userList: (prev.userList || []).filter((u: UserOption) => !visibleIds.has(u.value)),
        };
      });
      return;
    }
    setLeftSelectedIds(new Set(rawUsers.map((u) => u.id)));
    setForm((prev: any) => {
      const map = new Map((prev.userList || []).map((u: UserOption) => [u.value, u]));
      rawUsers.forEach((u) => map.set(u.id, { label: u.name, value: u.id }));
      return { ...prev, userList: Array.from(map.values()) };
    });
  }, [rawUsers, leftSelectedIds, setForm]);

  const handleRemoveAssigned = useCallback(
    (userId: string) => {
      setLeftSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
      upsertAssigned(userId, "", false);
    },
    [upsertAssigned]
  );

  const handleClearAll = useCallback(() => {
    setLeftSelectedIds(new Set());
    setForm((prev: any) => {
      return { ...prev, userList: [] };
    });
  }, [setForm]);

  const listLoading = isFetchingBase && !usersData;

  return (
    <div className="flex border border-lightGray rounded-2xl bg-gray-100 min-h-[75vh] max-h-[75vh] overflow-hidden">
      <div className="pl-5 pr-3 py-5 bg-white flex-[5] rounded-l-2xl flex flex-col min-w-0 min-h-0 border-r border-gray-201">
        <Filters
          search={search}
          onSearchChange={setSearch}
          cohortFilter={cohortFilter}
          onCohortChange={setCohortFilter}
          cohortOptions={cohortOptions}
          zoneFilter={zoneFilter}
          onZoneChange={setZoneFilter}
          regionFilter={regionFilter}
          onRegionChange={setRegionFilter}
          regionsOptions={regionsOptions}
          zonesOptions={zonesOptions}
        />
        <UserList
          onSelectAll={handleSelectAll}
          onToggleSelect={handleToggleSelect}
          users={listRows}
          isLoading={listLoading}
          cohorts={cohorts}
        />
      </div>
      <div className="pl-3 pr-5 py-5 rounded-r-2xl flex-[4] flex flex-col min-w-0 min-h-0 bg-gray-100">
        <AssignedUsersList userIdToName={userIdToName} users={form.userList || []} onRemove={handleRemoveAssigned} onClearAll={handleClearAll} />
      </div>
    </div>
  );
}
